import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  StarIcon as StarIconStroke
} from "@hugeicons-pro/core-stroke-rounded";
import { StarIcon as StarIconSolid } from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BaseInput, InputSize } from "shared/components/forms/BaseInput";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { cx } from "shared/utils/classnames";
import { useInboxEmbed } from "../context/useInboxEmbed";

const SEARCH_DEBOUNCE_MS = 300;

export type TemplateOption = { id: number; name: string; favorite?: boolean };

// Matches the API's case-insensitive A to Z (Postgres en_US ignores spaces
// and punctuation), so re-sorting the loaded page never reorders it.
const nameCollator = new Intl.Collator("en", {
  sensitivity: "base",
  ignorePunctuation: true
});

const byName = (a: TemplateOption, b: TemplateOption) =>
  nameCollator.compare(a.name, b.name);

// Splits the templates into Favorites and All templates (CRM-1882). A template
// stays in the group it had when this open of the list first showed it, so a
// star click moves its row only the next time the list opens; `firstSeen`
// remembers those groups and gains every template seen for the first time.
const groupByFirstSeen = <T extends TemplateOption>(
  items: T[],
  firstSeen: Map<number, boolean>
) => {
  items.forEach((item) => {
    if (!firstSeen.has(item.id)) firstSeen.set(item.id, Boolean(item.favorite));
  });
  return {
    favorites: items.filter((item) => firstSeen.get(item.id)).sort(byName),
    others: items.filter((item) => !firstSeen.get(item.id)).sort(byName)
  };
};

const FavoriteStar = ({
  favorite,
  onToggle
}: {
  favorite: boolean;
  onToggle: () => void;
}) => {
  const label = favorite ? "Remove from favorites" : "Add to favorites";
  return (
    <Tooltip placement="top">
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={favorite}
          // Keeps focus in the search box, where the arrow keys are handled.
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={cx(
            "mr-2 grid size-7 shrink-0 place-items-center rounded-md transition-colors",
            favorite
              ? "text-primary-500 hover:text-primary-600"
              : "text-content-300 hover:bg-alpha-80 hover:text-content-1000"
          )}
        >
          <HugeiconsIcon
            icon={favorite ? StarIconSolid : StarIconStroke}
            size={16}
            strokeWidth={1.5}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent className="pointer-events-none whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs text-white z-[1000]">
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export type TemplateSearchResult<T extends TemplateOption> = {
  items: T[];
  isLoading: boolean;
  isError: boolean;
};

export const TemplateSearchList = <T extends TemplateOption>({
  label,
  inputId,
  useTemplates,
  busy,
  onPick,
  onToggleFavorite,
  onClose
}: {
  label: string;
  inputId: string;
  useTemplates: (search: string) => TemplateSearchResult<T>;
  busy: boolean;
  onPick: (template: T) => void;
  onToggleFavorite: (template: T, favorite: boolean) => void;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [debounced] = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const { items: loaded, isLoading, isError } = useTemplates(debounced);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const firstSeenRef = useRef(new Map<number, boolean>());
  const { favorites, others } = useMemo(
    () => groupByFirstSeen(loaded, firstSeenRef.current),
    [loaded]
  );
  const items = useMemo(() => [...favorites, ...others], [favorites, others]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const pick = (template: T | undefined) => {
    if (!template || busy) return;
    onPick(template);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(items[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const renderBody = (): ReactNode => {
    if (isLoading)
      return (
        <div className="px-4 py-3 text-sm text-content-400">Loading...</div>
      );
    if (isError)
      return (
        <div className="px-4 py-3 text-sm text-danger-500">
          Failed to load templates
        </div>
      );
    if (items.length === 0)
      return (
        <div className="px-4 py-3 text-sm text-content-400">
          {debounced
            ? `No templates match "${debounced}"`
            : "No templates available"}
        </div>
      );
    const renderRow = (template: T, index: number, first: boolean) => {
      const active = index === activeIndex;
      const favorite = Boolean(template.favorite);
      return (
        <div key={template.id}>
          {first ? null : <div className="h-px w-full bg-alpha-40" />}
          <div
            role="option"
            aria-selected={active}
            aria-disabled={busy}
            data-index={index}
            title={template.name}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => pick(template)}
            className={cx(
              "group flex w-full items-center gap-2 text-left text-sm font-medium text-content-1000 transition-colors hover:bg-alpha-40",
              busy ? "cursor-not-allowed opacity-50" : "cursor-pointer",
              active ? "bg-alpha-40" : ""
            )}
          >
            <span className="min-w-0 flex-1 truncate py-2.5 pl-4">
              {template.name}
            </span>
            <div
              className={cx(
                "shrink-0",
                favorite || active ? "" : "invisible group-hover:visible"
              )}
            >
              <FavoriteStar
                favorite={favorite}
                onToggle={() => onToggleFavorite(template, !favorite)}
              />
            </div>
          </div>
        </div>
      );
    };
    const renderGroup = (heading: string, group: T[], offset: number) =>
      group.length > 0 ? (
        <div role="group" aria-label={heading}>
          <div
            aria-hidden
            className="px-4 pb-1 pt-2.5 text-xs font-bold text-content-300"
          >
            {heading}
          </div>
          {group.map((template, i) => renderRow(template, offset + i, i === 0))}
        </div>
      ) : null;
    return (
      <div
        ref={listRef}
        role="listbox"
        aria-label={label}
        className="max-h-[246px] overflow-y-auto"
      >
        {favorites.length > 0 ? (
          <>
            {renderGroup("Favorites", favorites, 0)}
            {renderGroup("All templates", others, favorites.length)}
          </>
        ) : (
          others.map((template, index) =>
            renderRow(template, index, index === 0)
          )
        )}
      </div>
    );
  };

  return (
    <div className="w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-alpha-100 bg-white shadow-[0_8px_8px_-4px_rgba(28,29,34,0.03),0_20px_24px_-4px_rgba(28,29,34,0.08)]">
      <div className="border-b border-alpha-40 p-3">
        <BaseInput
          id={inputId}
          size={InputSize.SM}
          Icon={Search01Icon}
          placeholder="Search templates"
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoFocus
        />
      </div>
      {renderBody()}
    </div>
  );
};

export const PickerShell = ({
  hidden,
  disabled,
  open,
  onOpenChange,
  children
}: {
  hidden: boolean;
  disabled: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) => {
  const embed = useInboxEmbed();
  if (hidden) return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="top-end">
      <PopoverTrigger
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (disabled) return;
          onOpenChange(!open);
        }}
      >
        <Button
          variant={ButtonVariant.BASE}
          size={embed?.compact ? ButtonSize.XS : ButtonSize.SM}
          disabled={disabled}
          className="!text-sm !font-bold shadow-xs"
        >
          Templates
        </Button>
      </PopoverTrigger>
      <PopoverContent className={embed?.menuZClass ?? "z-[12]"}>
        {children}
      </PopoverContent>
    </Popover>
  );
};
